import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  // Le fragment est rendu par Rails : sans préfixe de langue, la route
  // retombe sur I18n.default_locale et renvoie du FRANÇAIS à un
  // utilisateur anglais, à l'intérieur d'un écran par ailleurs anglais.
  // La langue se lit dans l'URL : la balise <html> ne porte pas de `lang`.
  get locale() {
    const segment = window.location.pathname.split("/")[1];
    return ["fr", "en"].includes(segment) ? segment : "fr";
  }

  static targets = [
    "modal",
    "form",
    "yearSelect",
    "details",
    "modalTitle",
    "trimestersContainer",
    "trimesterTemplate",
    "pedagogicMonthTemplate",
    "trimesterItem",
  ];

  connect() {
    console.log("Academic Year Manager controller connected");
    this.trimesterCounter = 0;
    this.pedagogicMonthCounters = {};
  }

  // Gestion du sélecteur d'année académique
  changeActiveYear(event) {
    const academicYearId = event.target.value;
    if (!academicYearId) return;

    fetch(`/${this.locale}/schools/settings/academic_years/${academicYearId}`, {
      headers: {
        Accept: "text/html",
        "X-Requested-With": "XMLHttpRequest",
      },
    })
      .then((response) => response.text())
      .then((html) => {
        this.detailsTarget.innerHTML = html;
      })
      .catch((error) =>
        console.error("Erreur lors du chargement de l'année académique:", error)
      );
  }

  // Gestion du modal
  openModal(event) {
    event.preventDefault();

    // Réinitialiser le formulaire
    this.formTarget.reset();

    // Vider les conteneurs de trimestres
    // On ne supprime que les éléments trimester-item, pas les templates
    const trimesterItems =
      this.trimestersContainerTarget.querySelectorAll(".trimester-item");
    trimesterItems.forEach((item) => item.remove());

    // Réinitialiser les compteurs
    this.trimesterCounter = 0;
    this.pedagogicMonthCounters = {};

    // Changer le titre du modal
    this.modalTitleTarget.textContent = this.modalTitleTarget.dataset.titleAdd;

    // Changer l'URL du formulaire pour la création
    this.formTarget.action = "/schools/settings/academic_years";
    this.formTarget.method = "post";

    // Supprimer le champ _method s'il existe
    const methodField = this.formTarget.querySelector('input[name="_method"]');
    if (methodField) methodField.remove();

    // Afficher le modal
    this.modalTarget.classList.remove("hidden");
  }

  closeModal(event) {
    event.preventDefault();
    this.modalTarget.classList.add("hidden");
  }

  closeModalOutside(event) {
    const element = this.modalTarget.firstElementChild;
    console.log({ element })
    if (event.target === element) {
      this.closeModal(event)
    }
  }

  // Édition d'une année académique
  editYear(event) {
    event.preventDefault();
    const academicYearId = event.currentTarget.getAttribute(
      "data-academic-year-manager-id"
    );

    // Récupérer les données de l'année académique
    fetch(`/${this.locale}/schools/settings/academic_years/${academicYearId}/edit`, {
      headers: {
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
    })
      .then((response) => response.json())
      .then((data) => {
        // Réinitialiser le formulaire
        this.formTarget.reset();

        // Vider les conteneurs de trimestres
        // On ne supprime que les éléments trimester-item, pas les templates
        const trimesterItems =
          this.trimestersContainerTarget.querySelectorAll(".trimester-item");
        trimesterItems.forEach((item) => item.remove());

        // Réinitialiser les compteurs
        this.trimesterCounter = 0;
        this.pedagogicMonthCounters = {};

        const academicYear = data.data;

        // Remplir le formulaire avec les données de l'année académique (robuste id/name)
        const startInput =
          document.getElementById("academic_year_start_date") ||
          this.formTarget.querySelector('input[name="academic_year[start_date]"]');
        const endInput =
          document.getElementById("academic_year_end_date") ||
          this.formTarget.querySelector('input[name="academic_year[end_date]"]');
        if (startInput) startInput.value = this.formatDate(academicYear.attributes.start_date);
        if (endInput) endInput.value = this.formatDate(academicYear.attributes.end_date);

        // is_current
        const currentInput = this.formTarget.querySelector('input[type="checkbox"][name="academic_year[is_current]"]');
        if (currentInput) currentInput.checked = !!academicYear.attributes.is_current;

        // Ajouter les trimestres
        if (data.included) {
          const trimesters = data.included.filter(
            (item) => item.type === "trimester"
          );
          const pedagogicMonths = data.included.filter(
            (item) => item.type === "pedagogic_month"
          );

          // Ajouter les trimestres
          trimesters.forEach((trimester) => {
            this.addTrimester();
            const trimesterIndex = this.trimesterCounter - 1;
            const trimesterItem = this.trimesterItemTargets[trimesterIndex];

            // Remplir les champs du trimestre
            trimesterItem.querySelector('input[name$="[id]"]').value =
              trimester.id;
            trimesterItem.querySelector('input[name$="[name]"]').value =
              trimester.attributes.name;
            trimesterItem.querySelector('input[name$="[start_date]"]').value =
              this.formatDate(trimester.attributes.start_date);
            trimesterItem.querySelector('input[name$="[end_date]"]').value =
              this.formatDate(trimester.attributes.end_date);

            // Ajouter les mois pédagogiques de ce trimestre
            const trimesterMonths = pedagogicMonths.filter(
              (month) =>
                month.attributes.trimester_id === parseInt(trimester.id)
            );
            const monthsContainer = trimesterItem.querySelector(
              ".pedagogic-months-container"
            );

            trimesterMonths.forEach((month) => {
              const monthElement = this.createPedagogicMonth(trimesterIndex);
              monthsContainer.appendChild(monthElement);

              // Remplir les champs du mois pédagogique
              const inputs = monthElement.querySelectorAll("input");
              inputs[0].value = month.id;
              inputs[1].value = month.attributes.name;
              inputs[2].value = this.formatDate(month.attributes.start_date);
              inputs[3].value = this.formatDate(month.attributes.end_date);
            });
          });
        }

        // Changer le titre du modal
        this.modalTitleTarget.textContent = this.modalTitleTarget.dataset.titleEdit;

        // Changer l'URL du formulaire pour la mise à jour
        this.formTarget.action = `/schools/settings/academic_years/${academicYearId}`;

        const form = document.getElementById("academic-year-form");
        form.insertAdjacentHTML(
          "beforeend",
          '<input type="hidden" name="_method" value="patch">'
        );

        // Ouvrir le modal
        this.modalTarget.classList.remove("hidden");
      })
      .catch((error) =>
        console.error(
          "Erreur lors de la récupération de l'année académique:",
          error
        )
      );
  }

  // Gestion des trimestres
  addTrimester(event) {
    if (event) event.preventDefault();

    // Cloner le template
    const template = this.trimesterTemplateTarget.content.cloneNode(true);

    // Remplacer l'index du trimestre
    const trimesterIndex = this.trimesterCounter++;
    this.replaceIndexInTemplate(
      template,
      "__TRIMESTER_INDEX__",
      trimesterIndex
    );

    // Initialiser le compteur de mois pédagogiques pour ce trimestre
    this.pedagogicMonthCounters[trimesterIndex] = 0;

    // Ajouter le trimestre au conteneur
    this.trimestersContainerTarget.appendChild(template);
  }

  removeTrimester(event) {
    event.preventDefault();
    const trimesterItem = event.target.closest(".trimester-item");
    trimesterItem.remove();
  }

  // Gestion des mois pédagogiques
  addPedagogicMonth(event) {
    event.preventDefault();
    const trimesterItem = event.target.closest(".trimester-item");
    const trimesterIndex = Array.from(this.trimesterItemTargets).indexOf(
      trimesterItem
    );

    if (trimesterIndex === -1) return;

    const monthElement = this.createPedagogicMonth(trimesterIndex);
    const monthsContainer = trimesterItem.querySelector(
      ".pedagogic-months-container"
    );
    monthsContainer.appendChild(monthElement);
  }

  createPedagogicMonth(trimesterIndex) {
    // Cloner le template
    const template = this.pedagogicMonthTemplateTarget.content.cloneNode(true);

    // Remplacer les index
    this.replaceIndexInTemplate(
      template,
      "__TRIMESTER_INDEX__",
      trimesterIndex
    );

    // Initialiser le compteur de mois pédagogiques pour ce trimestre si nécessaire
    if (!this.pedagogicMonthCounters[trimesterIndex]) {
      this.pedagogicMonthCounters[trimesterIndex] = 0;
    }

    const monthIndex = this.pedagogicMonthCounters[trimesterIndex]++;
    this.replaceIndexInTemplate(template, "__MONTH_INDEX__", monthIndex);

    return template.firstElementChild;
  }

  removePedagogicMonth(event) {
    event.preventDefault();
    const monthItem = event.target.closest(".pedagogic-month-item");
    monthItem.remove();
  }

  // Soumission du formulaire
  submitForm() {
    // La soumission est gérée par Rails
  }

  // Utilitaires
  replaceIndexInTemplate(template, placeholder, index) {
    const elements = template.querySelectorAll(
      "input, select, textarea, label"
    );
    elements.forEach((element) => {
      if (element.name) {
        element.name = element.name.replace(placeholder, index);
      }
      if (element.id) {
        element.id = element.id.replace(placeholder, index);
      }
      if (element.htmlFor) {
        element.htmlFor = element.htmlFor.replace(placeholder, index);
      }
    });
  }

  formatDate(dateString) {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toISOString().split("T")[0];
  }
}
